/**
 * GbcCustomerLabs — per-step webhook tracker for Goodbody quizzes.
 *
 * This file is fully self-contained. It auto-detects which quiz is on the page
 * and hooks into interactions via event delegation + MutationObserver.
 * NO changes to the quiz template files are required.
 *
 * Load via layout/theme.liquid or a global snippet:
 *   <script src="{{ 'gbc-customerlabs.js' | asset_url }}" defer></script>
 *
 * Tracked quizzes:
 *   1. Find My Test  → https://hook.customerlabs.co/source/find-my-test_quiz-2096/
 *   2. TruCheck      → https://hook.customerlabs.co/source/trucheck_quiz-7045/
 */
(function () {
  'use strict';

  var LOG_PREFIX = '[GBC:CustomerLabs]';

  // ==================================================================
  // CORE: Session, CLID, Webhook POST
  // ==================================================================

  function generateSessionId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'sess-' + Date.now() + '-' + Math.random().toString(36).slice(2, 11);
  }

  function getClid() {
    try {
      if (window.customerLabs && window.customerLabs.cid) return window.customerLabs.cid;
      if (window.customerLabs && typeof window.customerLabs.getCID === 'function') {
        return window.customerLabs.getCID();
      }
      if (window._clab && window._clab.cid) return window._clab.cid;
    } catch (e) {}

    try {
      var cookies = document.cookie.split(';');
      for (var i = 0; i < cookies.length; i++) {
        var c = cookies[i].trim();
        var eq = c.indexOf('=');
        if (eq < 1) continue;
        var name = c.substring(0, eq);
        var value = c.substring(eq + 1);
        if (/^cl[a-z0-9]*_uid$/i.test(name) && /^cl/i.test(value)) {
          return decodeURIComponent(value);
        }
        var lower = name.toLowerCase();
        if ((lower.indexOf('clab') !== -1 || lower.indexOf('customerlabs') !== -1) && value.length >= 8) {
          return decodeURIComponent(value);
        }
      }
    } catch (e) {}

    try {
      var params = new URLSearchParams(window.location.search);
      return params.get('clid') || params.get('clab_clid') || '';
    } catch (e) {}

    return '';
  }

  function postWebhook(url, payload) {
    var body = JSON.stringify(payload);

    // Prefer sendBeacon — fire-and-forget, survives page unload, no CORS preflight
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      var blob = new Blob([body], { type: 'text/plain' });
      var ok = navigator.sendBeacon(url, blob);
      console.log(LOG_PREFIX, ok ? 'SENT' : 'QUEUED', payload.step_name, payload.quiz_name);
      return;
    }

    // Fallback: fetch with no-cors + text/plain to avoid CORS preflight
    if (typeof fetch !== 'function') {
      console.warn(LOG_PREFIX, 'fetch unavailable, skipping');
      return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: body,
      mode: 'no-cors',
      keepalive: true
    })
      .then(function () {
        console.log(LOG_PREFIX, 'SENT', payload.step_name, payload.quiz_name);
      })
      .catch(function (err) {
        console.error(LOG_PREFIX, 'ERR', payload.step_name, err.message || err);
      });
  }

  function createTracker(webhookUrl, quizName) {
    var sessionId = generateSessionId();
    var clid = getClid();
    console.log(LOG_PREFIX, 'Tracker ready', quizName, 'session=' + sessionId);

    return {
      step: function (stepNumber, stepName, data) {
        if (!clid) clid = getClid();
        var payload = {
          event: quizName + '_step_' + stepNumber,
          step: stepNumber,
          step_name: stepName,
          quiz_name: quizName,
          session_id: sessionId,
          clid: clid,
          timestamp: new Date().toISOString(),
          page_url: window.location.href,
          data: data || {}
        };
        console.log(LOG_PREFIX, 'step=' + stepNumber, stepName, JSON.stringify(data || {}));
        postWebhook(webhookUrl, payload);
      }
    };
  }

  // ==================================================================
  // QUIZ 1: FIND MY TEST (gb-screen-* sections, data-gb-pick-key buttons)
  // Detected by: #gb-screen-welcome or .gb-stage
  // ==================================================================

  function bindFindMyTest() {
    var stage = document.querySelector('.gb-stage');
    if (!stage) return;

    var tracker = createTracker(
      'https://hook.customerlabs.co/source/find-my-test_quiz-2096/',
      'find_my_test'
    );

    var tracked = {};
    var answers = {};

    function getAnswers() {
      return JSON.parse(JSON.stringify(answers));
    }

    function syncFindMyTestAnswers(data) {
      if (!data) return;
      if (data.quiz_concern !== undefined) answers.q1 = data.quiz_concern || '';
      if (data.quiz_sub_concern !== undefined) answers.q2 = data.quiz_sub_concern || '';
      if (data.quiz_gender !== undefined) answers.q3 = data.quiz_gender || '';
      if (data.quiz_age_range !== undefined) answers.q4 = data.quiz_age_range || '';
    }

    function trackFindMyTestAnswer(key, value) {
      var stepMap = { q1: 1, q2: 2, q3: 3, q4: 4 };
      var nameMap = {
        q1: 'concern_selected',
        q2: 'sub_concern_selected',
        q3: 'gender_selected',
        q4: 'age_selected'
      };

      if (!stepMap[key] || !value) return;

      answers[key] = value;
      var state = getAnswers();
      state.answer = value;
      tracker.step(stepMap[key], nameMap[key], state);
    }

    function bindFindMyTestDataLayer() {
      window.dataLayer = window.dataLayer || [];
      if (window.dataLayer._gbcFindMyTestHooked) return;

      function processDataLayerItem(item) {
        if (!item || typeof item !== 'object') return;
        syncFindMyTestAnswers(item);
        if (item.event === 'quiz_question_answered') {
          trackFindMyTestAnswer(item.question_key, item.answer_value);
        }
      }

      window.dataLayer.forEach(processDataLayerItem);

      var originalPush = typeof window.dataLayer.push === 'function'
        ? window.dataLayer.push
        : Array.prototype.push;
      window.dataLayer.push = function () {
        for (var i = 0; i < arguments.length; i++) {
          processDataLayerItem(arguments[i]);
        }
        return originalPush.apply(window.dataLayer, arguments);
      };
      window.dataLayer._gbcFindMyTestHooked = true;
    }

    // Track "Start my assessment" click
    stage.addEventListener('click', function (e) {
      var startBtn = e.target.closest('[data-gb-action="start"]');
      if (startBtn && !tracked.started) {
        tracked.started = true;
        tracker.step(0, 'quiz_started', {});
      }
    });

    // The quiz already publishes semantic answer events to dataLayer.
    // Use those values so dynamic Q2 options do not need duplicated data attributes.
    bindFindMyTestDataLayer();

    // Track quiz completion via MutationObserver
    var resultsScreen = document.getElementById('gb-screen-results');
    if (resultsScreen) {
      var resultObserver = new MutationObserver(function () {
        if (resultsScreen.classList.contains('gb-active') && !tracked.completed) {
          tracked.completed = true;
          var state = getAnswers();
          var grid = document.getElementById('gb-productGrid');
          var productCards = grid ? grid.querySelectorAll('.gb-product-card') : [];
          state.matched_product_count = productCards.length;
          tracker.step(5, 'quiz_completed', state);
        }
      });
      resultObserver.observe(resultsScreen, { attributes: true, attributeFilter: ['class'] });
    }

    // Track product clicks
    stage.addEventListener('click', function (e) {
      var productBtn = e.target.closest('.gb-product-btn');
      if (!productBtn) return;
      var card = productBtn.closest('.gb-product-card');
      var name = card ? (card.querySelector('.gb-product-name') || {}).textContent : '';
      var url = productBtn.getAttribute('href') || '';
      tracker.step(6, 'product_clicked', {
        item_name: (name || '').trim(),
        item_url: url
      });
    });

    // Track retake
    var retakeBtn = document.getElementById('gb-retakeBtn');
    if (retakeBtn) {
      retakeBtn.addEventListener('click', function () {
        tracked = {};
        answers = {};
        tracker.step(7, 'quiz_retaken', {});
      });
    }
  }

  // ==================================================================
  // QUIZ 2: TRUCHECK (gb-quiz-root, .gb-slide sections, data-q buttons)
  // Detected by: #gbQuizRoot
  // ==================================================================

  function bindTruCheck() {
    var root = document.getElementById('gbQuizRoot');
    if (!root) return;

    var tracker = createTracker(
      'https://hook.customerlabs.co/source/trucheck_quiz-7045/',
      'trucheck'
    );

    var tracked = {};
    var lastAreas = [];

    // Track single-select option clicks (age, family, diagnosis)
    root.addEventListener('click', function (e) {
      var option = e.target.closest('.gb-option[data-q]');
      if (!option) return;

      var q = option.getAttribute('data-q');
      var v = option.getAttribute('data-value');
      var stepMap = { age: 1, family: 3, diagnosis: 4 };
      var nameMap = { age: 'age_selected', family: 'family_selected', diagnosis: 'diagnosis_selected' };

      if (!stepMap[q]) return;
      tracker.step(stepMap[q], nameMap[q], { answer: v });
    });

    // Track multi-select tile clicks (areas)
    root.addEventListener('click', function (e) {
      var tile = e.target.closest('.gb-tile[data-q="areas"]');
      if (!tile) return;

      // Small delay so the quiz updates its own selected state
      setTimeout(function () {
        var selected = [];
        root.querySelectorAll('.gb-tile.selected[data-value]').forEach(function (t) {
          selected.push(t.getAttribute('data-value'));
        });
        // Only fire if areas actually changed
        if (selected.join(',') !== lastAreas.join(',')) {
          lastAreas = selected.slice();
          tracker.step(2, 'areas_updated', { areas: selected });
        }
      }, 50);
    });

    // Track "Continue" / "See my result" clicks for next-step transitions
    root.addEventListener('click', function (e) {
      var nextBtn = e.target.closest('[data-action="next"]');
      if (!nextBtn || nextBtn.disabled) return;

      var slide = nextBtn.closest('.gb-slide');
      if (!slide) return;
      var slideKey = slide.getAttribute('data-slide');

      if (slideKey === 'q2' && !tracked.areasConfirmed) {
        tracked.areasConfirmed = true;
        var areas = [];
        root.querySelectorAll('.gb-tile.selected[data-value]').forEach(function (t) {
          areas.push(t.getAttribute('data-value'));
        });
        tracker.step(2, 'areas_confirmed', { areas: areas });
      }

      // eligible/ineligible outcomes handled exclusively by slide observers below
      // to avoid double-firing
    });

    // Observe slide transitions for eligible/ineligible/thankyou
    var slides = root.querySelectorAll('.gb-slide');
    slides.forEach(function (slide) {
      var slideKey = slide.getAttribute('data-slide');
      if (!slideKey) return;

      var observer = new MutationObserver(function () {
        if (!slide.classList.contains('active')) return;

        if (slideKey === 'eligible' && !tracked.eligible) {
          tracked.eligible = true;
          var ageEl = root.querySelector('.gb-option.selected[data-q="age"]');
          var famEl = root.querySelector('.gb-option.selected[data-q="family"]');
          var diagEl = root.querySelector('.gb-option.selected[data-q="diagnosis"]');
          tracker.step(5, 'eligible', {
            age: ageEl ? ageEl.getAttribute('data-value') : '',
            areas: lastAreas.slice(),
            family: famEl ? famEl.getAttribute('data-value') : '',
            diagnosis: diagEl ? diagEl.getAttribute('data-value') : ''
          });
        }

        if (slideKey === 'ineligible-age' && !tracked.ineligAge) {
          tracked.ineligAge = true;
          var ageEl2 = root.querySelector('.gb-option.selected[data-q="age"]');
          tracker.step(5, 'ineligible', {
            reason: 'age',
            age: ageEl2 ? ageEl2.getAttribute('data-value') : ''
          });
        }

        if (slideKey === 'ineligible-diagnosis' && !tracked.ineligDiag) {
          tracked.ineligDiag = true;
          var diagAge = root.querySelector('.gb-option.selected[data-q="age"]');
          var diagFam = root.querySelector('.gb-option.selected[data-q="family"]');
          var diagDiag = root.querySelector('.gb-option.selected[data-q="diagnosis"]');
          tracker.step(5, 'ineligible', {
            reason: 'diagnosis',
            age: diagAge ? diagAge.getAttribute('data-value') : '',
            areas: lastAreas.slice(),
            family: diagFam ? diagFam.getAttribute('data-value') : '',
            diagnosis: diagDiag ? diagDiag.getAttribute('data-value') : ''
          });
        }

        if (slideKey === 'thankyou' && !tracked.thankyou) {
          tracked.thankyou = true;
          tracker.step(7, 'thankyou_shown', {});
        }
      });
      observer.observe(slide, { attributes: true, attributeFilter: ['class'] });
    });

    // Track lead form submission
    var form = document.getElementById('gbLeadForm');
    if (form) {
      form.addEventListener('submit', function () {
        var firstName = (form.querySelector('[name="firstName"]') || {}).value || '';
        var lastName = (form.querySelector('[name="lastName"]') || {}).value || '';
        var email = (form.querySelector('[name="email"]') || {}).value || '';
        var phone = (form.querySelector('[name="phone"]') || {}).value || '';
        var dob = (form.querySelector('[name="dob"]') || {}).value || '';
        tracker.step(6, 'form_submitted', {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          dob: dob.trim()
        });
      });
    }
  }

  // ==================================================================
  // INIT — runs on DOMContentLoaded (or immediately if already loaded)
  // ==================================================================

  function init() {
    var foundQuiz = false;

    if (document.querySelector('.gb-stage')) {
      bindFindMyTest();
      foundQuiz = true;
    }

    if (document.getElementById('gbQuizRoot')) {
      bindTruCheck();
      foundQuiz = true;
    }

    if (!foundQuiz) {
      // No quiz on this page — silently exit
      return;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for manual use if needed
  window.GbcCustomerLabs = {
    createTracker: createTracker,
    getClid: getClid
  };

})();
